import { useEffect, useState } from "react";
import { ButtonLoader } from "../../../../components/uxCollections/Loaders";
import "../../../../styles/stores/makeTransactions/paymentWindow/paymentWindow.css";
import { Wallet, CreditCard, Landmark, Smartphone, Zap, Send, Loader2, CheckCircle2, XCircle } from "lucide-react";

// paymentType -> { label, Icon }. Mirrors the receivingAccount schema's
// enum (["cash","POS","bankTransfer","mobilePay","card","storelensePOS"]).
const PAYMENT_TYPE_META = {
  cash: { label: "Cash", Icon: Wallet },
  POS: { label: "POS", Icon: CreditCard },
  storelensePOS: { label: "StoreLense POS", Icon: Zap },
  card: { label: "Card", Icon: CreditCard },
  bankTransfer: { label: "Bank Transfer", Icon: Landmark },
  mobilePay: { label: "Mobile Money", Icon: Smartphone },
};

const CashPaymentOverlay = ({
  isOpen = true,
  onClose,
  onConfirm,
  amount = 0,
  balance = 5831993,
  sellItems,
  currency,
  total,
  cancel,
  transDetail,
  discount,
  vat,
  isLoading,
  payment, // payload from <PaymentMode onChange={...} /> — see PaymentMode.jsx
  pushToTerminal, // async ({ accountId, accountName, provider, amount, sellToken, postype, serialNumber }) => { success: boolean, message?: string }
  socket,
  transID,
}) => {
  // Tracks which partner accounts have already been successfully pushed
  // to their terminal, keyed by accountId.
  const [confirmedPartners, setConfirmedPartners] = useState({});
  // The in-flight / just-finished push shown in the animated overlay.
  // { entry, status: 'pushing' | 'success' | 'fail', message }
  const [pushState, setPushState] = useState(null);

  // A loan / part payment still goes through the exact same payment-type
  // and split rendering as a full payment — the only difference is that
  // the amount actually being collected right now is the loan's initial
  // payment (which is allowed to be 0), not the full sale total. The
  // Balance line below stays loan-only, since that's genuinely loan info.
  const isLoan = !transDetail?.cash;
  const dueAmount = isLoan ? Number(transDetail?.loanDetails?.initialPay || 0) : Number(total);

  const isSplit = Boolean(payment?.isSplit);
  const typeMeta = payment?.paymentType ? PAYMENT_TYPE_META[payment.paymentType] : null;
  const TypeIcon = typeMeta?.Icon || Wallet;

  const title = isSplit ? "Confirm split payment" : `Confirm ${typeMeta?.label || "cash"} payment`;

  // Push a single payment (or one split row) to its partner terminal and
  // animate the result. Success marks the account confirmed so the
  // relevant Confirm button unlocks; failure leaves the overlay up with
  // Retry/Cancel so nothing silently moves on.

  useEffect(() => {
    const handleKudaPaymentSuccessful = async (data) => {
      console.log("Payment successful:", data);

      // 1. Show success
      setPushState({
        status: "success",
        message: data?.message || "Payment successful",
      });

      // 2. Wait 1.5 seconds, then clear
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setPushState(null);

      // 3. Sell after success message is cleared
      if (!isSplit) {
        await sellItems();
      }
    };

    socket.on("kudaPaymentSuccessful", handleKudaPaymentSuccessful);

    return () => {
      socket.off("kudaPaymentSuccessful", handleKudaPaymentSuccessful);
    };
  }, []);

  if (!isOpen) return null;

  const handlePushToTerminal = async (entry) => {
    setPushState({ entry, status: "pushing" });

    //emit socket

    socket.emit("joinPaymentRoom", transID);

    try {
      const result = await pushToTerminal({
        accountId: entry.accountId,
        accountName: entry.accountName,
        provider: entry.provider,
        amount: entry.amount,
        sellToken: entry.isPartner?.sellToken,
        postype: entry.isPartner?.postype,
        reference: entry.reference,
        serialNumber: entry.serialNumber,
      });

      const success = result?.success === true;

      // on confirmation from web hock
      /*    */

      if (success) {
        setConfirmedPartners((prev) => ({ ...prev, [entry.accountId]: true }));

        setPushState({ entry, status: success ? "waiting" : "fail", message: result?.message });
        // setTimeout(() => setPushState(null), 1500);
      } else {
        setPushState({ entry, status: "fail", message: result?.message || "Something unexpected happened" });
      }
    } catch (err) {
      setPushState({ entry, status: "fail", message: err?.message || "Couldn't reach the terminal" });
    }
  };

  const singleIsPartner = Boolean(payment?.isPartner?.hasIntgration) && !isSplit;
  const singleNeedsPush = singleIsPartner && !confirmedPartners[payment?.accountId];

  const partnerSplits = isSplit ? payment.splits.filter((s) => s.isPartner?.hasIntgration) : [];
  const allPartnerSplitsConfirmed = partnerSplits.every((s) => confirmedPartners[s.accountId]);

  const confirmLabel = isLoading
    ? "Creating..."
    : isSplit
      ? "Confirm split payment"
      : payment?.paymentType === "cash" || !payment?.paymentType
        ? "Confirm cash reception"
        : `Confirm ${typeMeta?.label || "payment"} reception`;

  return (
    <div className='cash-payment-overlay'>
      <div className='cash-payment-backdrop' onClick={() => cancel(false)} />
      <div className='cash-payment-modal'>
        <div className='cash-payment-content'>
          {/* Header */}
          <div className='cash-payment-header'>
            <h2 className='cash-payment-title'>{title}</h2>
          </div>

          {/* Illustration */}
          <div className='cash-payment-illustration'>
            <div className='payment-icon-container'>
              <div className='businessman-icon'>
                <div className='person-body'></div>
                <div className='person-head'></div>
                <div className='dollar-badge'>{isSplit ? <TypeIcon size={14} strokeWidth={2.5} /> : currency}</div>
              </div>
            </div>
          </div>

          {isSplit ? (
            // ---------- Split payment: show the breakdown ----------
            <div className='cash-payment-split-section'>
              <ul className='cash-payment-split-list'>
                {payment.splits.map((split) => {
                  const meta = PAYMENT_TYPE_META[split.paymentType] || PAYMENT_TYPE_META.cash;
                  const SplitIcon = meta.Icon;
                  const partner = Boolean(split.isPartner?.hasIntgration);
                  const pushed = confirmedPartners[split.accountId];
                  return (
                    <li className='cash-payment-split-item' key={split.accountId}>
                      <span className='cash-payment-split-icon' style={{ background: split.accountColor || "#0f2a47" }}>
                        <SplitIcon size={14} strokeWidth={2.5} />
                      </span>
                      <span className='cash-payment-split-account'>
                        {split.accountName}
                        <span className='cash-payment-split-type'>
                          {meta.label}
                          {partner && (
                            <span className='cash-payment-partner-tag'>
                              <Zap size={9} strokeWidth={3} />
                              Partner
                            </span>
                          )}
                        </span>
                      </span>
                      <span className='cash-payment-split-amount'>
                        {currency}
                        {Number(split.amount).toLocaleString()}
                      </span>
                      {partner &&
                        (pushed ? (
                          <span className='cash-payment-split-pushed' title='Confirmed by terminal'>
                            <CheckCircle2 size={15} strokeWidth={2.5} />
                          </span>
                        ) : (
                          <button
                            type='button'
                            className='cash-payment-split-push-btn'
                            onClick={() => {
                              handlePushToTerminal({ ...split, amount: split.amount });
                            }}
                            aria-label={`Push payment to ${split.provider || "terminal"}`}
                            title={`Push to ${split.provider || "terminal"}`}
                          >
                            <Send size={14} strokeWidth={2.5} />
                          </button>
                        ))}
                    </li>
                  );
                })}
              </ul>
              <div className='cash-payment-split-total'>
                <span>Total</span>
                <span>
                  {currency}
                  {dueAmount.toLocaleString()}
                </span>
              </div>
              {partnerSplits.length > 0 && !allPartnerSplitsConfirmed && (
                <div className='cash-payment-partner-note'>
                  <Zap size={13} strokeWidth={2.5} />
                  You can push a partner payment to its terminal, or just confirm the sale directly.
                </div>
              )}
            </div>
          ) : (
            // ---------- Single payment: amount + which account was used ----------
            <div className='cash-payment-amount-section'>
              <div className='amount-display'>
                {currency}
                {dueAmount.toLocaleString()}
              </div>
              {payment?.accountName && (
                <div className='cash-payment-account-chip'>
                  <span className='cash-payment-account-chip__icon' style={{ background: payment.accountColor || "#0f2a47" }}>
                    <TypeIcon size={12} strokeWidth={2.5} />
                  </span>
                  <span className='cash-payment-account-chip__label'>via {payment.accountName}</span>
                </div>
              )}
              {singleIsPartner && (
                <div className='cash-payment-partner-note'>
                  <Zap size={13} strokeWidth={2.5} />
                  {singleNeedsPush
                    ? `Push this payment to ${payment.provider || "the terminal"} to confirm it.`
                    : `Confirmed via ${payment.provider || "the terminal"}.`}
                </div>
              )}
            </div>
          )}

          {/* Balance — loan/part-payment only */}
          {isLoan && (
            <div className='balance-section'>
              <span className='balance-text'>
                Balance: {currency}
                {transDetail.loanDetails.balance.toLocaleString()}
              </span>
            </div>
          )}

          {/* Confirm Button */}
          <div className='cash-payment-actions'>
            {singleNeedsPush ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  width: "100%",
                }}
              >
                <button
                  type='button'
                  disabled={isLoading}
                  className='confirm-cash-btn'
                  onClick={() =>
                    handlePushToTerminal({
                      accountId: payment.accountId,
                      accountName: payment.accountName,
                      provider: payment.provider,
                      isPartner: payment.isPartner,
                      reference: payment.reference,
                      amount: dueAmount,
                      serialNumber: payment.serialNumber,
                    })
                  }
                >
                  <Send size={15} strokeWidth={2.5} />
                  Push to {`${payment.provider}'s terminal` || "terminal"}
                </button>
                <button
                  type='button'
                  disabled={isLoading}
                  className='confirm-cash-btn'
                  style={{ background: "#ffffff", color: "#2463EB", border: "1px solid rgb(99, 159, 222)", flex: 1 }}
                  onClick={() => {
                    setConfirmedPartners((prev) => ({ ...prev, [payment.accountId]: true }));
                    sellItems();
                  }}
                >
                  {`Confirm ${typeMeta?.label || "payment"} reception manually`}
                </button>
              </div>
            ) : (
              <button
                disabled={isLoading}
                className='confirm-cash-btn'
                onClick={() => {
                  console.log("sellItems called", isSplit, partnerSplits.length, allPartnerSplitsConfirmed);
                  sellItems();
                }}
              >
                {confirmLabel}
              </button>
            )}
          </div>
        </div>

        {/* ---------- Terminal push animation overlay ---------- */}
        {pushState && (
          <div className='terminal-push-overlay'>
            <div className='terminal-push-icon-wrap'>
              {pushState.status === "pushing" && <Loader2 size={40} strokeWidth={2} className='terminal-push-spinner' />}
              {pushState.status === "waiting" && <Loader2 size={40} strokeWidth={2} className='terminal-push-spinner' />}

              {pushState.status === "success" && <CheckCircle2 size={40} strokeWidth={2} className='terminal-push-icon--success' />}
              {pushState.status === "fail" && <XCircle size={40} strokeWidth={2} className='terminal-push-icon--fail' />}
            </div>
            <div className='terminal-push-title'>
              {pushState.status === "pushing" && `Pushing to ${pushState.entry.provider || "terminal"}…`}
              {pushState.status === "waiting" && `Waiting to collect payment from ${pushState.entry.provider || "terminal"}…`}

              {pushState.status === "success" && "Payment received"}
              {pushState.status === "fail" && "Payment failed"}
            </div>
            <div className='terminal-push-subtitle'>
              {pushState.status === "pushing" && `Waiting for confirmation from ${pushState.entry.accountName}`}
              {pushState.status === "waiting" && (pushState.message || `Waiting to collect payment via ${pushState.entry.accountName} terminal`)}

              {pushState.status === "success" && `Confirmed via ${pushState?.entry?.accountName || "your terminal"}`}
              {pushState.status === "fail" && (pushState.message || `${pushState.entry.accountName} didn't confirm the payment`)}
            </div>
            {pushState.status === "fail" && (
              <div className='terminal-push-actions'>
                <button type='button' className='terminal-push-btn terminal-push-btn--ghost' onClick={() => setPushState(null)}>
                  Cancel
                </button>
                <button type='button' className='terminal-push-btn terminal-push-btn--primary' onClick={() => handlePushToTerminal(pushState.entry)}>
                  Try again
                </button>
              </div>
            )}
            {pushState.status === "waiting" && (
              <div className='terminal-push-actions'>
                <button type='button' className='terminal-push-btn terminal-push-btn--ghost' onClick={() => setPushState(null)}>
                  I have collected this payment
                </button>
                {/*   <button type='button' className='terminal-push-btn terminal-push-btn--primary' onClick={() => handlePushToTerminal(pushState.entry)}>
                  Try again
                </button> */}
              </div>
            )}
            {pushState.status === "pushing" && (
              <div className='terminal-push-actions'>
                <button type='button' className='terminal-push-btn terminal-push-btn--ghost' onClick={() => setPushState(null)}>
                  Collect manually
                </button>
                {/*   <button type='button' className='terminal-push-btn terminal-push-btn--primary' onClick={() => handlePushToTerminal(pushState.entry)}>
                  Try again
                </button> */}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CashPaymentOverlay;
